import { toDate, toDateString } from '@/utils/date';

import {
    DEFAULT_DAYPART_TAG,
    DEFAULT_WEIGHT_UNIT_TAG,
    type OptoDayPartTagType,
    QUERY_DAYPART_TAG,
    QUERY_SELECTED_FISH_GROUP_ID,
    QUERY_WEIGHT_UNIT_TAG,
} from './params';

/** Shape accepted by `setFilter`. Each field is optional; `null` clears it. */
export interface CageFilterUpdate {
    from?: string | null;
    to?: string | null;
    daypartTag?: OptoDayPartTagType;
    weightUnitTag?: string;
    /** Reserved slot — populated when the fish-group selector UI lands. */
    selectedFishGroupId?: number | null;
}

/** URL query-params shape produced by the reducers. */
export type CageFilterUrlParams = {
    from?: string | null;
    to?: string | null;
    [QUERY_DAYPART_TAG]?: string | null;
    [QUERY_WEIGHT_UNIT_TAG]?: string | null;
    [QUERY_SELECTED_FISH_GROUP_ID]?: string | null;
};

type FilterReducer<K extends keyof CageFilterUpdate> = (
    params: CageFilterUrlParams,
    value: CageFilterUpdate[K]
) => CageFilterUrlParams;

type FilterReducers = { [K in keyof CageFilterUpdate]-?: FilterReducer<K> };

const FILTER_REDUCERS: FilterReducers = {
    from: (params, value) => ({
        ...params,
        from: value == null ? null : toDateString(toDate(value)) || null,
    }),
    to: (params, value) => ({
        ...params,
        to: value == null ? null : toDateString(toDate(value)) || null,
    }),
    daypartTag: (params, value) => ({
        ...params,
        [QUERY_DAYPART_TAG]: value === DEFAULT_DAYPART_TAG ? null : (value ?? null),
    }),
    weightUnitTag: (params, value) => ({
        ...params,
        [QUERY_WEIGHT_UNIT_TAG]: value === DEFAULT_WEIGHT_UNIT_TAG ? null : (value ?? null),
    }),
    selectedFishGroupId: (params, value) => ({
        ...params,
        [QUERY_SELECTED_FISH_GROUP_ID]: value == null ? null : String(value),
    }),
};

/** The URL params a filter update writes. Keys the filter does not know are ignored. */
export function filterUpdateToParams(update: CageFilterUpdate): CageFilterUrlParams {
    return (Object.keys(update) as Array<keyof CageFilterUpdate>)
        .filter((key) => key in FILTER_REDUCERS)
        .reduce<CageFilterUrlParams>((acc, key) => {
            const reducer = FILTER_REDUCERS[key] as FilterReducer<typeof key>;
            return reducer(acc, update[key]);
        }, {});
}
